import { type IMapGeoObject } from '@cian/frontend-newbuilding-map-component';
import type { TCustomBalloonFetchHtmlFunction } from '@cian/frontend-newbuilding-map-component/es/types';

import { type FC, useEffect, useState } from 'react';
import * as ReactDOMServer from 'react-dom/server';
import { useSelector } from 'react-redux';

import { Map as MapComponent } from 'shared/components/Map';
import { type RoomsInfoItem } from 'shared/components/RoomsList';
import { getLocationCenterCoordinates } from 'shared/selectors/coordinates';
import { getFilteredResidentialComplexes } from 'shared/selectors/residentialComplexes/getFilteredResidentialComplexes';
import { getResidentialComplexes } from 'shared/selectors/residentialComplexes/getResidentialComplexes';
import { newbuildingsPlusBlocksPikSelector } from 'shared/selectors/specialPromosBlocks/newbuildingsPlusBlocksPikSelector';
import { useApplicationContext } from 'shared/utils/applicationContext';
import { extractUndefined } from 'shared/utils/extractUndefined';

import { getPins } from './helpers/getPins';
import { MapBalloon } from './internal/components/MapBalloon';

const defaultRoomsInfo = Array.of<RoomsInfoItem>();

export const Map: FC = () => {
  const { config } = useApplicationContext();
  const { hash, list } = useSelector(getResidentialComplexes);
  const [pinTypes, setPinTypes] = useState<string[]>([]);
  const filteredComplexes = useSelector(getFilteredResidentialComplexes);
  const { center, zoom } = useSelector(getLocationCenterCoordinates);
  const newbuildingsPlusBlocksPik = useSelector(newbuildingsPlusBlocksPikSelector);

  const mapApiKey = config.get<string>('builders.YandexApiKey') || '';

  const handleGeoObjectsFirstFetch = (): Promise<IMapGeoObject[]> => {
    const pins = getPins(list);

    return Promise.resolve(pins);
  };

  const handleBalloonHtmlFirstFetch: TCustomBalloonFetchHtmlFunction = geoObject => {
    const numberedId = Number(geoObject.options.id);
    const complex = hash[numberedId];

    if (!complex) {
      return { html: Promise.reject(new Error('Не верный тип пина!')) };
    }

    const roomsInfo = complex.roomsInfo || defaultRoomsInfo;
    const subtitle = extractUndefined(complex.finishInfo?.label);
    const promoLabel = extractUndefined(complex.promoLabel);
    const imageUrl = complex.imgUrl || complex.fullImgUrl || '';

    const isSeriaPlus =
      !!newbuildingsPlusBlocksPik.length && newbuildingsPlusBlocksPik.some(pik => pik.newbuildingId === complex.id);

    return {
      closeSelector: '#close',
      html: Promise.resolve(
        ReactDOMServer.renderToStaticMarkup(
          <MapBalloon
            imageUrl={imageUrl}
            isSeriaPlus={isSeriaPlus}
            promoLabel={promoLabel}
            roomsInfo={roomsInfo}
            subtitle={subtitle}
            title={complex.title}
            transports={complex.transports}
            url={complex.publicUrl}
          />,
        ),
      ),
    };
  };

  useEffect(() => {
    setPinTypes(filteredComplexes.map(complex => String(complex.id)));
  }, [filteredComplexes]);

  return (
    <MapComponent
      apiKey={mapApiKey}
      currentTypes={pinTypes}
      pinCoordinates={center}
      zoom={zoom}
      onCustomBalloonHtmlFetch={handleBalloonHtmlFirstFetch}
      onGeoObjectsFirstFetch={handleGeoObjectsFirstFetch}
    />
  );
};
